import React, { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { IoIosArrowDown } from 'react-icons/io'
import { AiOutlinePlus, AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { useTimerGuard } from '../../../../hooks/useTimerGuard'
import BlockTimerModal from '../../../../components/Timer/BlockTimerModal'
import './styles.css'

interface PlanSelectorProps {
  currentPlan: IStudyPlan | null
  plans: IStudyPlan[]
  onSelectPlan: (planId: string) => void
  onDeletePlan?: (planId: string) => Promise<void>
  isDarkMode?: boolean
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  currentPlan,
  plans,
  onSelectPlan,
  onDeletePlan,
  isDarkMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null)
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const { checkAndBlock } = useTimerGuard()

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelectPlan = (planId: string) => {
    const action = () => {
      onSelectPlan(planId)
      setIsOpen(false)
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    action()
  }

  const handleCreateNew = () => {
    const action = () => {
      setIsOpen(false)
      history.push('/planejamento/novo')
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    action()
  }

  const handleEditPlan = (planId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    const action = () => {
      setIsOpen(false)
      // Define o plano como atual antes de navegar
      onSelectPlan(planId)
      history.push(`/planejamento/${planId}`)
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    action()
  }

  const handleDeletePlan = async (planId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (!onDeletePlan) return

    const action = async () => {
      const confirmed = window.confirm(
        'Tem certeza que deseja deletar este plano de estudos? Esta ação não pode ser desfeita.'
      )

      if (!confirmed) return

      try {
        setDeletingPlanId(planId)
        await onDeletePlan(planId)
        setIsOpen(false)
      } catch (error) {
        console.error('Error deleting plan:', error)
        alert('Erro ao deletar plano de estudos. Tente novamente.')
      } finally {
        setDeletingPlanId(null)
      }
    }

    if (
      checkAndBlock(() => {
        setPendingAction(() => action)
        setIsBlockModalOpen(true)
      })
    ) {
      return
    }

    await action()
  }

  const handleCloseBlockModal = () => {
    setIsBlockModalOpen(false)
    setPendingAction(null)
  }

  const handleFinishSession = () => {
    setIsBlockModalOpen(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  return (
    <div
      className={`plan-selector-container ${isDarkMode ? 'dark' : 'light'}`}
      ref={dropdownRef}
      data-tour="dashboard-plan-selector"
    >
      <button
        className={`plan-selector-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="plan-selector-name">{currentPlan?.name || 'Selecione um plano'}</span>
        <IoIosArrowDown
          size={20}
          className="plan-selector-icon"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: '0.3s',
          }}
        />
      </button>

      {isOpen && (
        <div className="plan-selector-dropdown">
          {plans.length === 0 ? (
            <div className="plan-selector-empty">
              <p>Nenhum plano criado ainda</p>
            </div>
          ) : (
            plans.map((plan) => (
              <div
                key={plan._id}
                className={`plan-selector-item ${currentPlan?._id === plan._id ? 'active' : ''}`}
                onClick={() => handleSelectPlan(plan._id)}
              >
                <div className="plan-selector-item-content">
                  <strong>{plan.name || 'Sem nome'}</strong>
                  {plan.createdAt && (
                    <div className="plan-selector-item-date">
                      Criado em: {new Date(plan.createdAt).toLocaleDateString('pt-BR')}
                    </div>
                  )}
                </div>

                <div className="plan-selector-item-actions">
                  <button
                    className="plan-selector-action-button"
                    onClick={(e) => handleEditPlan(plan._id, e)}
                    title="Editar plano"
                    disabled={deletingPlanId === plan._id}
                  >
                    <AiOutlineEdit size={18} />
                  </button>

                  {onDeletePlan && (
                    <button
                      className="plan-selector-action-button danger"
                      onClick={(e) => handleDeletePlan(plan._id, e)}
                      title="Deletar plano"
                      disabled={deletingPlanId === plan._id}
                    >
                      {deletingPlanId === plan._id ? '...' : <AiOutlineDelete size={18} />}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}

          <div className="plan-selector-divider" />

          <button className="plan-selector-create-button" onClick={handleCreateNew}>
            <AiOutlinePlus size={20} />
            <span>Criar novo plano</span>
          </button>
        </div>
      )}

      <BlockTimerModal
        isOpen={isBlockModalOpen}
        onClose={handleCloseBlockModal}
        onFinish={handleFinishSession}
      />
    </div>
  )
}

export default PlanSelector
